<template>
  <v-sheet min-height="400px">
    <div  class="mx-6" ref="chartContainer"></div>
  </v-sheet>
</template>

<script>
import Highcharts from 'highcharts'
import exportingInit from 'highcharts/modules/exporting'

exportingInit(Highcharts)

export default {
  name: 'PtmScoreScatterPlot',
  props: {
    tdata: {
      type: Array,
      required: true
    },
    xField: {
      type: String,
      default: 'publication_score'
    },
    yField: {
      type: String,
      default: 'data_score'
    },
    colorField: {
      type: String,
      default: null
    },
    showLabels: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.renderChart()
  },
  watch: {
    tdata() {
      this.renderChart()
    },
    xField() {
      this.renderChart()
    },
    yField() {
      this.renderChart()
    },
    colorField() {
      this.renderChart()
    },
    showLabels() {
      this.renderChart()
    }

  },
  methods: {
    formatLabel(field) {
      if (!field) return ''
      return field
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase())
    },
    renderChart() {
      const dataByType = {}
      let minValue = null
      let maxValue = null

      if (this.colorField) {
        const values = this.tdata
            .map(d => Number(d[this.colorField]))
            .filter(v => !isNaN(v))

        if (values.length > 0) {
          minValue = Math.min(...values)
          maxValue = Math.max(...values)
        }
      }

      let labelSet = new Set();

      if (this.showLabels) {
        const valid = this.tdata
            .map((d, idx) => {
              const x = Number(d[this.xField]);
              const y = Number(d[this.yField]);
              if (isNaN(x) || isNaN(y)) return null;

              return {
                key: d.internal_id,
                absX: Math.abs(x),
                absY: Math.abs(y),
                idx
              };
            })
            .filter(Boolean);

        const cutoff = Math.ceil(valid.length * 0.2);

        [...valid]
            .sort((a, b) => (b.absX - a.absX) || (a.idx - b.idx))
            .slice(0, cutoff)
            .forEach(d => labelSet.add(d.key));

        [...valid]
            .sort((a, b) => (b.absY - a.absY) || (a.idx - b.idx))
            .slice(0, cutoff)
            .forEach(d => labelSet.add(d.key));
      }



      for (const point of this.tdata) {
        const { internal_id, ptm_type, id } = point
        const x = Number(point[this.xField])
        const y = Number(point[this.yField])
        if (isNaN(x) || isNaN(y)) continue

        if (!dataByType[ptm_type]) dataByType[ptm_type] = []
        const value = this.colorField ? Number(point[this.colorField]) : null

        dataByType[ptm_type].push({
          x,
          y,
          value: isNaN(value) ? null : value,
          name: id,
          internal_id,
          ptm_type,
          showLabel: this.showLabels && labelSet.has(internal_id)
        })
      }

      const series = Object.entries(dataByType).map(([ptm_type, data]) => ({
        name: ptm_type,
        data,
        colorKey: this.colorField ? 'value' : undefined
      }))


      const xTitle = this.formatLabel(this.xField)
      const yTitle = this.formatLabel(this.yField)
      const colorTitle=this.formatLabel(this.colorField)
      const hasNegative =
          minValue !== null &&
          maxValue !== null &&
          minValue < 0

      const colorAxis = this.colorField
          ? (
              hasNegative
                  ? {
                    min: minValue,
                    max: maxValue,
                    threshold: 0,
                    minColor: '#2166ac',
                    maxColor: '#b2182b',
                    title: { text: this.formatLabel(this.colorField) }
                  }
                  : {
                    min: minValue,
                    max: maxValue,
                    minColor: '#f7fbff',
                    maxColor: '#b2182b',
                    title: { text: this.formatLabel(this.colorField) }
                  }
          )
          : null
      Highcharts.chart(this.$refs.chartContainer, {
        chart: {
          type: 'scatter',
          zoomType: 'xy',
          spacingTop: 20,
          spacingRight: 10,
          spacingBottom: 10,
          spacingLeft: 10
        },
        colorAxis: colorAxis,
        title: { text: '' },
        xAxis: {
          title: { text: xTitle },
          lineWidth: 1,
          lineColor: '#000000',
          gridLineWidth: 1,
          tickInterval: 0.2,
          gridLineDashStyle: 'Solid'
        },
        yAxis: {
          title: { text: yTitle },
          lineWidth: 1,
          lineColor: '#000000',
          gridLineWidth: 1,
          tickInterval: 0.2,
          gridLineDashStyle: 'Solid'
        },
        legend: { enabled: false },
        tooltip: {
          formatter: function () {
            let tooltip = `
      <b>${this.point.name}</b><br/>
      ${xTitle}: ${this.x.toFixed(3)}<br/>
      ${yTitle}: ${this.y.toFixed(3)}
    `

            if (this.point.value !== null && this.point.value !== undefined) {
              tooltip += `<br/>Color by ${colorTitle}: ${this.point.value.toFixed(3)}`
            }

            return tooltip
          }
        },
        plotOptions: {
          scatter: {
            marker: {
              radius: 4,
              lineWidth: 0.2,
              lineColor: '#333'
            },
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              formatter: function () {
                return this.point.showLabel ? this.point.name : null
              },

              style: {
                fontSize: '10px',
                color: '#000000',
                textOutline: 'none'
              }
            },
            point: {
              events: {
                click: function () {
                  const base_url = window.location.origin
                  const url = `${base_url}/#/single-search/${this.internal_id}?ptm_type=${encodeURIComponent(this.ptm_type)}`
                  window.open(url, '_blank')
                }
              }
            }
          }
        },

        series,
        exporting: {
          enabled: true,
          sourceWidth: 600,
          sourceHeight: 400,
          scale: 8,
          buttons: {
            contextButton: {
              x: 10,
              y: -28,
              menuItems: ['downloadPNG', 'downloadSVG']
            }
          }
        }
      })

    }
  }
}
</script>
